import React, { useState, useEffect } from 'react';
import AppLayout from '../components/AppLayout';
import { Package, Plus, Edit2, Trash2, Check, X, DollarSign, ChevronDown, ChevronUp, Eye, EyeOff, AlertTriangle } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const productTypes = {
  membership: { label: 'Membership', color: 'text-[#A2FF00]', bg: 'bg-[#A2FF00]/20' },
  addon: { label: 'Add-on', color: 'text-blue-400', bg: 'bg-blue-500/20' },
  service: { label: 'Service', color: 'text-purple-400', bg: 'bg-purple-500/20' }
};

const billingFrequencies = {
  one_time: 'One-time',
  monthly: 'Monthly',
  annual: 'Annual',
  consultancy: 'Consultancy'
};

const ProductsManagement = () => {
  const { isDark } = useTheme();
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [expandedProducts, setExpandedProducts] = useState({});
  const [showInactive, setShowInactive] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    product_type: 'membership',
    billing_frequency: 'annual',
    price_gbp: '',
    charge_vat: false,
    vat_rate: 0.20,
    is_active: true,
    sort_order: 0,
    variants: []
  });

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/products?include_inactive=true`, { credentials: 'include' });
      if (response.ok) {
        setProducts(await response.json());
      }
    } catch (error) {
      console.error('Failed to fetch products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const payload = {
      ...formData,
      price_gbp: parseFloat(formData.price_gbp) || 0,
      sort_order: parseInt(formData.sort_order) || 0,
      variants: formData.variants.map(v => ({
        ...v,
        price_gbp: parseFloat(v.price_gbp) || 0
      }))
    };
    
    try {
      const url = editingProduct
        ? `${BACKEND_URL}/api/products/${editingProduct.product_id}`
        : `${BACKEND_URL}/api/products`;
      
      const response = await fetch(url, {
        method: editingProduct ? 'PATCH' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        fetchProducts();
        closeModal();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to save product');
      }
    } catch (error) {
      console.error('Save error:', error);
      alert('Failed to save product');
    }
  };

  const deleteProduct = async (productId, permanent = false) => {
    if (permanent) {
      // Permanent delete - handled by confirmation modal
      try {
        const response = await fetch(`${BACKEND_URL}/api/products/${productId}?permanent=true`, {
          method: 'DELETE',
          credentials: 'include'
        });
        if (response.ok) {
          fetchProducts();
          setDeleteConfirm(null);
        } else {
          const error = await response.json();
          alert(error.detail || 'Failed to delete product');
        }
      } catch (error) {
        console.error('Delete error:', error);
        alert('Failed to delete product');
      }
    } else {
      // Soft delete (deactivate)
      if (!window.confirm('Are you sure you want to deactivate this product?')) return;
      
      try {
        const response = await fetch(`${BACKEND_URL}/api/products/${productId}`, {
          method: 'DELETE',
          credentials: 'include'
        });
        if (response.ok) {
          fetchProducts();
        }
      } catch (error) {
        console.error('Delete error:', error);
      }
    }
  };

  const toggleActive = async (product) => {
    try {
      await fetch(`${BACKEND_URL}/api/products/${product.product_id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ is_active: !product.is_active })
      });
      fetchProducts();
    } catch (error) {
      console.error('Toggle error:', error);
    }
  };

  const toggleProductExpanded = (productId) => {
    setExpandedProducts(prev => ({
      ...prev,
      [productId]: !prev[productId]
    }));
  };

  const openModal = (product = null) => {
    if (product) {
      setEditingProduct(product);
      setFormData({
        name: product.name,
        description: product.description || '',
        product_type: product.product_type,
        billing_frequency: product.billing_frequency,
        price_gbp: product.price_gbp.toString(),
        charge_vat: product.charge_vat || false,
        vat_rate: product.vat_rate || 0.20,
        is_active: product.is_active,
        sort_order: product.sort_order,
        variants: product.variants || []
      });
    } else {
      setEditingProduct(null);
      setFormData({
        name: '',
        description: '',
        product_type: 'membership',
        billing_frequency: 'annual',
        price_gbp: '',
        charge_vat: false,
        vat_rate: 0.20,
        is_active: true,
        sort_order: 0,
        variants: []
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingProduct(null);
  };

  const addVariant = () => {
    setFormData({
      ...formData,
      variants: [
        ...formData.variants,
        {
          name: '',
          billing_frequency: 'monthly',
          price_gbp: '',
          is_default: formData.variants.length === 0
        }
      ]
    });
  };

  const updateVariant = (index, field, value) => {
    const newVariants = [...formData.variants];
    newVariants[index] = { ...newVariants[index], [field]: value };
    
    // If setting as default, unset others
    if (field === 'is_default' && value) {
      newVariants.forEach((v, i) => {
        if (i !== index) v.is_default = false;
      });
    }
    
    setFormData({ ...formData, variants: newVariants });
  };

  const removeVariant = (index) => {
    const newVariants = formData.variants.filter((_, i) => i !== index);
    // If we removed the default, set first as default
    if (formData.variants[index]?.is_default && newVariants.length > 0) {
      newVariants[0].is_default = true;
    }
    setFormData({ ...formData, variants: newVariants });
  };

  if (loading) {
    return (
      <AppLayout title="Products">
        <div className="flex items-center justify-center h-64">
          <div className="text-[#A2FF00] text-xl">Loading...</div>
        </div>
      </AppLayout>
    );
  }

  // Filter products based on showInactive toggle
  const filteredProducts = showInactive 
    ? products 
    : products.filter(p => p.is_active);

  const inactiveCount = products.filter(p => !p.is_active).length;

  return (
    <AppLayout title="Products">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} style={{ fontFamily: 'Unbounded, sans-serif' }}>
              Products
            </h1>
            <p className="text-gray-400 mt-1">Manage products and pricing variants for proposals</p>
          </div>
          <div className="flex items-center gap-3">
            {/* Show Inactive Toggle */}
            {inactiveCount > 0 && (
              <button
                onClick={() => setShowInactive(!showInactive)}
                className={`flex items-center gap-2 px-3 py-2 rounded-sm transition-colors ${
                  showInactive 
                    ? 'bg-orange-500/20 text-orange-400 border border-orange-500/30' 
                    : isDark ? 'bg-white/5 text-gray-400 border border-white/10 hover:bg-white/10' : 'bg-gray-100 text-gray-600 border border-gray-200 hover:bg-gray-200'
                }`}
              >
                {showInactive ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                {showInactive ? 'Showing All' : `${inactiveCount} Inactive`}
              </button>
            )}
            <button
              onClick={() => openModal()}
              className={`flex items-center gap-2 px-4 py-2 ${isDark ? 'bg-[#A2FF00] hover:bg-[#8EFF00]' : 'bg-green-500 hover:bg-green-600'} text-black font-bold rounded-sm transition-colors`}
            >
              <Plus className="w-5 h-5" />
              Add Product
            </button>
          </div>
        </div>

        {/* Products Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredProducts.map((product) => {
            const type = productTypes[product.product_type] || productTypes.service;
            const hasVariants = product.variants && product.variants.length > 0;
            const isExpanded = expandedProducts[product.product_id];
            
            return (
              <div
                key={product.product_id}
                className={`${isDark ? 'bg-[#111]' : 'bg-white'} border rounded-sm ${
                  product.is_active ? (isDark ? 'border-white/10' : 'border-gray-200') : 'border-red-500/30 opacity-60'
                }`}
              >
                <div className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Package className={`w-5 h-5 ${isDark ? 'text-[#A2FF00]' : 'text-green-600'}`} />
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${type.bg} ${type.color}`}>
                        {type.label}
                      </span>
                      {hasVariants && (
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${isDark ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-600'}`}>
                          {product.variants.length} variants
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => openModal(product)}
                        className={`p-1.5 ${isDark ? 'text-gray-400 hover:text-[#A2FF00] hover:bg-[#A2FF00]/10' : 'text-gray-500 hover:text-green-600 hover:bg-green-50'} rounded transition-colors`}
                        title="Edit"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => toggleActive(product)}
                        className={`p-1.5 rounded transition-colors ${
                          product.is_active
                            ? 'text-green-400 hover:bg-green-500/20'
                            : 'text-red-400 hover:bg-red-500/20'
                        }`}
                        title={product.is_active ? 'Deactivate' : 'Activate'}
                      >
                        {product.is_active ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                      </button>
                      {/* Permanent Delete - Admin Only */}
                      {user?.role === 'admin' && !product.is_active && (
                        <button
                          onClick={() => setDeleteConfirm(product)}
                          className="p-1.5 text-red-400 hover:bg-red-500/20 rounded transition-colors"
                          title="Permanently Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                  
                  <h3 className={`${isDark ? 'text-white' : 'text-gray-900'} font-medium mb-1`}>{product.name}</h3>
                  {product.description && (
                    <p className="text-gray-400 text-sm mb-3 line-clamp-2">{product.description}</p>
                  )}
                  
                  {/* Default Price */}
                  <div className={`flex items-center justify-between mt-3 pt-3 border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                    <span className="text-gray-400 text-sm">
                      {billingFrequencies[product.billing_frequency]}
                    </span>
                    <span className={`${isDark ? 'text-[#A2FF00]' : 'text-green-600'} font-bold text-lg`}>
                      £{product.price_gbp.toLocaleString()}
                    </span>
                  </div>
                  
                  {/* Variants Toggle */}
                  {hasVariants && (
                    <button
                      onClick={() => toggleProductExpanded(product.product_id)}
                      className={`w-full mt-3 pt-3 border-t ${isDark ? 'border-white/10' : 'border-gray-200'} flex items-center justify-between text-sm ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                      <span>View pricing variants</span>
                      {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                  )}
                </div>
                
                {/* Expanded Variants */}
                {hasVariants && isExpanded && (
                  <div className={`border-t ${isDark ? 'border-white/10 bg-black/30' : 'border-gray-200 bg-gray-50'} p-4 space-y-2`}>
                    {product.variants.map((variant, idx) => (
                      <div key={variant.variant_id || idx} className={`flex items-center justify-between p-2 rounded ${isDark ? 'bg-white/5' : 'bg-white'}`}>
                        <div>
                          <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{variant.name}</span>
                          <span className={`ml-2 text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                            ({billingFrequencies[variant.billing_frequency]})
                          </span>
                          {variant.is_default && (
                            <span className="ml-2 px-1.5 py-0.5 text-xs bg-[#A2FF00]/20 text-[#A2FF00] rounded">Default</span>
                          )}
                        </div>
                        <span className={`font-bold ${isDark ? 'text-[#A2FF00]' : 'text-green-600'}`}>
                          £{variant.price_gbp.toLocaleString()}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {products.length === 0 && (
          <div className={`text-center py-12 ${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-200'} border rounded-sm`}>
            <Package className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <h3 className={`text-lg font-medium ${isDark ? 'text-white' : 'text-gray-900'} mb-2`}>No products yet</h3>
            <p className="text-gray-400 mb-4">Add your first product to include in proposals</p>
            <button
              onClick={() => openModal()}
              className={`px-4 py-2 ${isDark ? 'bg-[#A2FF00] hover:bg-[#8EFF00]' : 'bg-green-500 hover:bg-green-600'} text-black font-bold rounded-sm transition-colors`}
            >
              Add Product
            </button>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className={`fixed inset-0 ${isDark ? 'bg-black/80' : 'bg-black/50'} flex items-center justify-center z-50 p-4`}>
          <div className={`${isDark ? 'bg-[#0A0A0A] border-white/10' : 'bg-white border-gray-200'} border rounded-sm w-full max-w-2xl max-h-[90vh] overflow-y-auto`}>
            <div className={`p-6 border-b ${isDark ? 'border-white/10' : 'border-gray-200'} sticky top-0 ${isDark ? 'bg-[#0A0A0A]' : 'bg-white'} z-10`}>
              <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} style={{ fontFamily: 'Unbounded, sans-serif' }}>
                {editingProduct ? 'Edit Product' : 'Add Product'}
              </h2>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className={`w-full ${isDark ? 'bg-[#111] border-white/20 text-white' : 'bg-gray-50 border-gray-300 text-gray-900'} border rounded-sm px-3 py-2 focus:border-[#A2FF00] outline-none`}
                  placeholder="Premium Membership"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={2}
                  className={`w-full ${isDark ? 'bg-[#111] border-white/20 text-white' : 'bg-gray-50 border-gray-300 text-gray-900'} border rounded-sm px-3 py-2 focus:border-[#A2FF00] outline-none`}
                  placeholder="Product description..."
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">Type *</label>
                  <select
                    value={formData.product_type}
                    onChange={(e) => setFormData({ ...formData, product_type: e.target.value })}
                    className={`w-full ${isDark ? 'bg-[#111] border-white/20 text-white' : 'bg-gray-50 border-gray-300 text-gray-900'} border rounded-sm px-3 py-2 focus:border-[#A2FF00] outline-none`}
                  >
                    <option value="membership">Membership</option>
                    <option value="addon">Add-on</option>
                    <option value="service">Service</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">Default Billing *</label>
                  <select
                    value={formData.billing_frequency}
                    onChange={(e) => setFormData({ ...formData, billing_frequency: e.target.value })}
                    className={`w-full ${isDark ? 'bg-[#111] border-white/20 text-white' : 'bg-gray-50 border-gray-300 text-gray-900'} border rounded-sm px-3 py-2 focus:border-[#A2FF00] outline-none`}
                  >
                    <option value="one_time">One-time</option>
                    <option value="monthly">Monthly</option>
                    <option value="annual">Annual</option>
                    <option value="consultancy">Consultancy (50% + 5-day billing)</option>
                  </select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">Default Price (GBP) *</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">£</span>
                    <input
                      type="number"
                      value={formData.price_gbp}
                      onChange={(e) => setFormData({ ...formData, price_gbp: e.target.value })}
                      required
                      min="0"
                      step="0.01"
                      className={`w-full ${isDark ? 'bg-[#111] border-white/20 text-white' : 'bg-gray-50 border-gray-300 text-gray-900'} border rounded-sm pl-8 pr-3 py-2 focus:border-[#A2FF00] outline-none`}
                      placeholder="0.00"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">Sort Order</label>
                  <input
                    type="number"
                    value={formData.sort_order}
                    onChange={(e) => setFormData({ ...formData, sort_order: e.target.value })}
                    min="0"
                    className={`w-full ${isDark ? 'bg-[#111] border-white/20 text-white' : 'bg-gray-50 border-gray-300 text-gray-900'} border rounded-sm px-3 py-2 focus:border-[#A2FF00] outline-none`}
                  />
                </div>
              </div>

              {/* VAT Settings */}
              <div className={`p-4 rounded ${isDark ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-gray-200'} border`}>
                <div className="flex items-center justify-between">
                  <div>
                    <label className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Charge VAT</label>
                    <p className="text-xs text-gray-400 mt-0.5">Enable to add VAT to this product&apos;s price</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.charge_vat}
                      onChange={(e) => setFormData({ ...formData, charge_vat: e.target.checked })}
                      className="sr-only peer"
                    />
                    <div className={`w-11 h-6 ${isDark ? 'bg-gray-700' : 'bg-gray-300'} peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#A2FF00]`}></div>
                  </label>
                </div>
                {formData.charge_vat && (
                  <div className="mt-3 flex items-center gap-3">
                    <label className="text-sm text-gray-400">VAT Rate:</label>
                    <select
                      value={formData.vat_rate}
                      onChange={(e) => setFormData({ ...formData, vat_rate: parseFloat(e.target.value) })}
                      className={`${isDark ? 'bg-[#111] border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'} border rounded-sm px-2 py-1 text-sm focus:border-[#A2FF00] outline-none`}
                    >
                      <option value={0.20}>20% (Standard)</option>
                      <option value={0.05}>5% (Reduced)</option>
                      <option value={0}>0% (Zero-rated)</option>
                    </select>
                  </div>
                )}
              </div>

              {/* Pricing Variants Section */}
              <div className={`border-t ${isDark ? 'border-white/10' : 'border-gray-200'} pt-4 mt-4`}>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h3 className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Pricing Variants</h3>
                    <p className="text-xs text-gray-400">Add monthly/annual options for customer choice</p>
                  </div>
                  <button
                    type="button"
                    onClick={addVariant}
                    className={`flex items-center gap-1 px-3 py-1.5 text-sm ${isDark ? 'bg-white/10 hover:bg-white/20 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'} rounded transition-colors`}
                  >
                    <Plus className="w-4 h-4" />
                    Add Variant
                  </button>
                </div>

                {formData.variants.length === 0 ? (
                  <div className={`text-center py-6 ${isDark ? 'bg-white/5' : 'bg-gray-50'} rounded border ${isDark ? 'border-white/10' : 'border-gray-200'} border-dashed`}>
                    <p className="text-gray-400 text-sm">No variants added</p>
                    <p className="text-gray-500 text-xs mt-1">Add variants to offer monthly/annual payment options</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {formData.variants.map((variant, index) => (
                      <div key={index} className={`p-3 rounded ${isDark ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-gray-200'} border`}>
                        <div className="flex items-start gap-3">
                          <div className="flex-1 grid grid-cols-3 gap-3">
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">Variant Name</label>
                              <input
                                type="text"
                                value={variant.name}
                                onChange={(e) => updateVariant(index, 'name', e.target.value)}
                                placeholder="e.g., Monthly"
                                className={`w-full ${isDark ? 'bg-[#111] border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'} border rounded-sm px-2 py-1.5 text-sm focus:border-[#A2FF00] outline-none`}
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">Billing</label>
                              <select
                                value={variant.billing_frequency}
                                onChange={(e) => updateVariant(index, 'billing_frequency', e.target.value)}
                                className={`w-full ${isDark ? 'bg-[#111] border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'} border rounded-sm px-2 py-1.5 text-sm focus:border-[#A2FF00] outline-none`}
                              >
                                <option value="monthly">Monthly</option>
                                <option value="annual">Annual</option>
                                <option value="one_time">One-time</option>
                                <option value="consultancy">Consultancy</option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">Price (GBP)</label>
                              <input
                                type="number"
                                value={variant.price_gbp}
                                onChange={(e) => updateVariant(index, 'price_gbp', e.target.value)}
                                placeholder="0.00"
                                min="0"
                                step="0.01"
                                className={`w-full ${isDark ? 'bg-[#111] border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'} border rounded-sm px-2 py-1.5 text-sm focus:border-[#A2FF00] outline-none`}
                              />
                            </div>
                          </div>
                          <div className="flex items-center gap-2 pt-5">
                            <label className="flex items-center gap-1 text-xs text-gray-400 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={variant.is_default}
                                onChange={(e) => updateVariant(index, 'is_default', e.target.checked)}
                                className="w-3 h-3"
                              />
                              Default
                            </label>
                            <button
                              type="button"
                              onClick={() => removeVariant(index)}
                              className="p-1 text-red-400 hover:bg-red-500/20 rounded"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="w-4 h-4"
                />
                <label htmlFor="is_active" className="text-sm text-gray-400">Active (visible in proposals)</label>
              </div>
              
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className={`flex-1 px-4 py-2 ${isDark ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'} ${isDark ? 'text-white' : 'text-gray-700'} rounded-sm transition-colors`}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={`flex-1 px-4 py-2 ${isDark ? 'bg-[#A2FF00] hover:bg-[#8EFF00]' : 'bg-green-500 hover:bg-green-600'} text-black font-bold rounded-sm transition-colors`}
                >
                  {editingProduct ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className={`${isDark ? 'bg-[#1a1a1a]' : 'bg-white'} rounded-sm shadow-2xl max-w-md w-full p-6`}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-400" />
              </div>
              <div>
                <h3 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Permanently Delete Product?
                </h3>
                <p className="text-gray-400 text-sm">This action cannot be undone</p>
              </div>
            </div>
            
            <div className={`p-4 rounded ${isDark ? 'bg-red-500/10 border-red-500/30' : 'bg-red-50 border-red-200'} border mb-4`}>
              <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {deleteConfirm.name}
              </p>
              <p className="text-gray-400 text-sm mt-1">
                {deleteConfirm.product_type} • £{deleteConfirm.price_gbp?.toLocaleString()}
              </p>
            </div>
            
            <p className="text-gray-400 text-sm mb-6">
              This will permanently remove the product from the database. Any proposals using this product may be affected.
            </p>
            
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className={`flex-1 px-4 py-2 ${isDark ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'} rounded-sm transition-colors`}
              >
                Cancel
              </button>
              <button
                onClick={() => deleteProduct(deleteConfirm.product_id, true)}
                className="flex-1 px-4 py-2 bg-red-500 hover:bg-red-600 text-white font-bold rounded-sm transition-colors"
              >
                Delete Forever
              </button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
};

export default ProductsManagement;
